import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bookmark, Trash2, Share2, Eye, Calendar, Leaf, 
  AlertTriangle, Search, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  AlertDialog, AlertDialogAction, AlertDialogCancel, 
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter, 
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger 
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SavedRecipes() {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSavedRecipes();
  }, []);

  const loadSavedRecipes = () => {
    setLoading(true);
    // Load from localStorage
    const saved = JSON.parse(localStorage.getItem('acupedia_saved_recipes') || '[]');
    setRecipes(saved);
    setLoading(false);
  };

  const handleViewRecipe = (recipe) => {
    localStorage.setItem('acupedia_current_recipe', JSON.stringify(recipe));
    navigate(`/recipe/${recipe.id || 'saved'}`);
  };

  const handleDeleteRecipe = async (recipe, index) => {
    // Remove from localStorage
    const saved = JSON.parse(localStorage.getItem('acupedia_saved_recipes') || '[]');
    saved.splice(index, 1);
    localStorage.setItem('acupedia_saved_recipes', JSON.stringify(saved));
    setRecipes(saved);
    
    // Try to delete from server too
    try {
      if (recipe.id) {
        await axios.delete(`${API}/recipes/saved/${recipe.id}?user_id=anonymous`);
      }
    } catch (error) {
      // Ignore server errors, local delete is enough
    }
    
    toast.success('Rețeta a fost ștearsă');
  };

  const handleShare = (recipe) => {
    const shareUrl = `${window.location.origin}/recipe/shared?share=${recipe.share_code}`;
    if (navigator.share) {
      navigator.share({
        title: recipe.name,
        text: `Am descoperit o rețetă de ceai terapeutic pe Acupedia! 🍵\n\n${recipe.name}`,
        url: shareUrl
      });
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Link copiat în clipboard!');
    }
  };

  const filteredRecipes = recipes.filter(recipe => 
    recipe.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    recipe.scop?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ro-RO', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="py-8 md:py-12 animate-fade-in">
      <div className="container-acupedia">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-acupedia-gold flex items-center justify-center">
                <Bookmark className="w-5 h-5 text-white" />
              </div>
              <h1 className="section-header mb-0">Rețetele Mele Salvate</h1>
            </div>
            <p className="section-subheader">
              Accesează rapid rețetele tale preferate
            </p>
          </div>
          <Badge variant="outline" className="px-4 py-2 text-sm self-start md:self-auto">
            {recipes.length} rețete salvate
          </Badge>
        </div>

        {/* Search */}
        {recipes.length > 0 && (
          <div className="mb-8">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
              <Input
                placeholder="Caută în rețetele salvate..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 input-acupedia"
                data-testid="search-saved-input"
              />
            </div>
          </div>
        )}

        {/* Recipes Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="skeleton h-48 rounded-xl"></div>
            ))}
          </div>
        ) : filteredRecipes.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRecipes.map((recipe, index) => (
              <div
                key={index}
                className="card-acupedia p-6 hover:shadow-lg transition-all duration-300"
              >
                {/* Header */}
                <div className="flex items-start justify-between gap-3 mb-4">
                  <h3 className="font-heading text-lg text-acupedia-teal line-clamp-2">
                    {recipe.name}
                  </h3>
                  <Badge className="bg-acupedia-gold/10 text-acupedia-gold border-0 flex-shrink-0">
                    <Bookmark className="w-3 h-3 mr-1" />
                    Salvată
                  </Badge>
                </div>

                {/* Description */}
                <p className="text-sm text-stone-600 line-clamp-2 mb-4">
                  {recipe.scop}
                </p>

                {/* Ingredients preview */}
                <div className="flex flex-wrap gap-1 mb-4">
                  {recipe.ingredients?.slice(0, 3).map((ing, i) => (
                    <span 
                      key={i}
                      className="text-xs bg-stone-100 text-stone-600 px-2 py-1 rounded"
                    >
                      {ing.herb_name}
                    </span>
                  ))}
                  {recipe.ingredients?.length > 3 && (
                    <span className="text-xs text-stone-400">
                      +{recipe.ingredients.length - 3}
                    </span>
                  )}
                </div>

                {/* Date */}
                <div className="flex items-center gap-2 text-xs text-stone-400 mb-4">
                  <Calendar className="w-3 h-3" />
                  {formatDate(recipe.saved_at)}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-4 border-t border-stone-100">
                  <Button
                    size="sm"
                    onClick={() => handleViewRecipe(recipe)}
                    className="flex-1 bg-acupedia-teal hover:bg-acupedia-teal-light text-white"
                    data-testid={`view-recipe-${index}`}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Vezi
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleShare(recipe)}
                    data-testid={`share-recipe-${index}`}
                  >
                    <Share2 className="w-4 h-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-recipe-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Șterge rețeta?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Această acțiune nu poate fi anulată. Rețeta "{recipe.name}" 
                          va fi eliminată din lista ta de salvări.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Anulează</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDeleteRecipe(recipe, index)}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          Șterge
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        ) : recipes.length > 0 ? (
          /* No search results */
          <div className="text-center py-16 bg-stone-50 rounded-xl">
            <Search className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl text-stone-600 mb-2">
              Nu am găsit rețete
            </h3>
            <p className="text-stone-500">
              Încearcă să modifici căutarea
            </p>
          </div>
        ) : (
          /* Empty state */
          <div className="text-center py-16 bg-stone-50 rounded-xl">
            <Bookmark className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl text-stone-600 mb-2">
              Nu ai rețete salvate
            </h3>
            <p className="text-stone-500 mb-6 max-w-md mx-auto">
              Generează o rețetă și apasă pe "Salvează" pentru a o păstra aici.
            </p>
            <Button
              onClick={() => navigate('/')}
              className="btn-primary"
              data-testid="go-generate-btn"
            >
              <Leaf className="w-5 h-5 mr-2" />
              Generează prima rețetă
            </Button>
          </div>
        )}

        {/* Info */}
        {recipes.length > 0 && (
          <div className="mt-8 p-4 bg-cyan-50 rounded-lg border border-cyan-200">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-cyan-700">
                Rețetele salvate sunt stocate local pe acest dispozitiv. 
                Pentru a le accesa de pe alte dispozitive, folosește funcția de partajare.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
